import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { Select } from "antd";
import PageHeader from "../../../molecules/PatientDetailsV2/PageHeader/PageHeader";
import Input from "../../../atoms/Input/Input";
import Button from "../../../atoms/Button/Button";
import Table from "../../../atoms/Table/Table";
import {
  uploadAssessmentRecording,
  getAssessmentRecordingHistory,
} from "../../../services/rest/zyvelorAPI";
import { toast } from "react-toastify";
import "./AssessmentRecapPage.scss";
import { formatDateTime } from "../../../utils/date-and-time";
import { RefreshIcon } from "../../../assets/icons";
import { getAssessmentOptions, getStatusLabel, validateMeetingLink } from "../../../helpers/assessment";
import { STATUS } from "../../../constants/common";
import { useCurrentPatientId } from "../../../hooks/useCurrentPatient";

interface RecordingHistoryItem {
  generation_id: string;
  meeting_link: string;
  assessment_id: number;
  status: string;
  created_at: string;
  updated_at: string;
}

const successStatuses: string[] = [STATUS.SUCCESS, STATUS.COMPLETED];
const processingStatuses: string[] = [STATUS.IN_PROGRESS, STATUS.INPROGRESS, STATUS.PROCESSING, STATUS.STARTED];

const AssessmentRecapPage: React.FC = () => {
  const params = useParams();
  const patientId = useCurrentPatientId();
  const healthCoachId =
    params.healthCoachId || localStorage.getItem("health_coach_id");

  const [meetingLink, setMeetingLink] = useState("");
  const [assessmentNumber, setAssessmentNumber] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [history, setHistory] = useState<RecordingHistoryItem[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [assessmentOptions, setAssessmentOptions] = useState<
    Array<{ label: string; value: string }>
  >(getAssessmentOptions());
  const [errors, setErrors] = useState<{
    meetingLink?: string;
    assessmentNumber?: string;
  }>({});

  useEffect(() => {
    fetchHistory();
    setAssessmentOptions(getAssessmentOptions());
  }, [patientId]);

  const handleMeetingLinkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setMeetingLink(value);
    if (errors.meetingLink) {
      setErrors({ ...errors, meetingLink: undefined });
    }
  };

  const handleAssessmentChange = (value: string, option: any) => {
    setAssessmentNumber(value);
    if (errors.assessmentNumber) {
      setErrors({ ...errors, assessmentNumber: undefined });
    }
  };

  const handleSubmit = async () => {
    // Reset errors
    const newErrors: { meetingLink?: string; assessmentNumber?: string } = {};

    // Validate meeting link
    if (!meetingLink.trim()) {
      newErrors.meetingLink = "Meeting link is required";
    } else if (!validateMeetingLink(meetingLink)) {
      newErrors.meetingLink = "Please enter a valid Google Meet or Zoom link";
    }

    // Validate assessment number
    if (assessmentNumber === "") {
      newErrors.assessmentNumber = "Please select an assessment";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsUploading(true);
    try {
      const response = await uploadAssessmentRecording({
        patient_id: patientId,
        health_coach_id: healthCoachId,
        assessment_id: parseInt(assessmentNumber),
        meeting_link: meetingLink.trim(),
      });

      if (response.status === "Success") {
        toast.success(response.message || "Recording uploaded successfully");
        setMeetingLink("");
        setAssessmentNumber("");
        setErrors({});
        // Refresh history
        fetchHistory();
      } else {
        toast.error(response.message || "Failed to upload recording");
      }
    } catch (error: any) {
      console.error("Error uploading recording:", error);
      toast.error(
        error?.message || "Failed to upload recording. Please try again."
      );
    } finally {
      setIsUploading(false);
    }
  };

  const fetchHistory = async () => {
    if (!patientId) return;

    setIsLoadingHistory(true);
    try {
      const response = await getAssessmentRecordingHistory({
        patient_id: patientId,
      });

      if (response.status === "Success" && Array.isArray(response.data)) {
        setHistory(response.data);
      } else {
        setHistory([]);
      }
    } catch (error) {
      console.error("Error fetching history:", error);
      setHistory([]);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const getStatusIcon = (status: string) => {
    const statusLower = status.toLowerCase();
  
    if (successStatuses.includes(statusLower)) {
      return (
        <span className="status-icon status-icon-success">
          <CheckCircleOutlined style={{ color: "#19BB7A", fontSize: "16px" }} />
        </span>
      );
    } else if (processingStatuses.includes(statusLower)) {
      return (
        <span className="status-icon status-icon-processing">
          <ClockCircleOutlined style={{ color: "#1890FF", fontSize: "16px" }} />
        </span>
      );
    } else {
      return (
        <span className="status-icon status-icon-failed">
          <CloseCircleOutlined style={{ color: "#F04545", fontSize: "16px" }} />
        </span>
      );
    }
  };

  const getStatusBadge = (status: string) => {
    const statusLower = status.toLowerCase();
    let className = "status-badge ";
    let displayText = status;

    if (successStatuses.includes(statusLower)) {
      className += "status-success";
      displayText = getStatusLabel(STATUS.SUCCESS);
    } else if (processingStatuses.includes(statusLower)) {
      className += "status-processing";
      displayText = getStatusLabel(STATUS.PROCESSING);
    } else {
      className += "status-failed";
      displayText = getStatusLabel(STATUS.FAILED);
    }

    return <span className={className}>{displayText}</span>;
  };

  const getAssessmentLabel = (assessmentId: number) => {
    if (assessmentId === 0) return "Main Assessment";
    return `Assessment ${assessmentId}`;
  };

  const columns = [
    {
      label: "Meeting Link",
      accessor: "meeting_link",
      render: (value: string, row: RecordingHistoryItem) => (
        <div className="meeting-link-cell">
          {getStatusIcon(row.status)}
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            className="meeting-link"
          >
            {value}
          </a>
        </div>
      ),
    },
    {
      label: "Assessment No.",
      accessor: "assessment_id",
      render: (value: number) => getAssessmentLabel(value),
    },
    {
      label: "Upload Date",
      accessor: "created_at",
      render: (value: string) => formatDateTime({ timestamp: value }),
    },
    {
      label: "Status",
      accessor: "status",
      render: (value: string) => getStatusBadge(value),
    },
  ];

  return (
    <div className="assessment-recap-page">
      <PageHeader title="Assessment Recap" />
      {/* Upload Assessment Recording Section */}
      <div className="upload-section">
        <h2 className="section-title">Upload Assessment Recording</h2>
        <div className="form-container">
          <div className="form-field">
            <Input
              label="Meeting Link"
              placeholder="Enter meeting link (e.g., https://meet.google.com/...)"
              value={meetingLink}
              onChange={handleMeetingLinkChange}
              required
              className={errors.meetingLink ? "error" : ""}
              type="text"
              name="meetingLink"
              id="meetingLink"
            />
            {errors.meetingLink && (
              <span className="error-message">{errors.meetingLink}</span>
            )}
          </div>
          <div className="form-field">
            <label className="input-label" htmlFor="assessment-number">
              Assessment Number{" "}
              <span style={{ color: "red", fontSize: "16px" }}>*</span>
            </label>
            <Select
              id="assessment-number"
              className={`assessment-select ${
                errors.assessmentNumber ? "error" : ""
              }`}
              size="middle"
              placeholder="Select assessment number"
              value={assessmentNumber || undefined}
              onChange={handleAssessmentChange}
              disabled={assessmentOptions.length === 0}
              style={{ width: "100%" }}
            >
              {assessmentOptions.map((option: any) => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
            {errors.assessmentNumber && (
              <span className="error-message">{errors.assessmentNumber}</span>
            )}
          </div>
          <div className="submit-button-container">
            <Button
              onClick={handleSubmit}
              loading={isUploading}
              disabled={isUploading}
              className="upload-button"
            >
              Submit Link
            </Button>
          </div>
        </div>
      </div>

      {/* Past Uploads Section */}
      <div className="history-section">
        <div className="section-header">
          <h2 className="section-title">Past Uploads</h2>
          <Button
            variant="outlined"
            onClick={fetchHistory}
            disabled={isLoadingHistory}
            className="refresh-button"
          >
            <RefreshIcon />
            Refresh
          </Button>
        </div>
        <div className="table-wrapper-custom">
          <Table
            data={history}
            columns={columns}
            isLoading={isLoadingHistory}
            border={false}
            showPagination={false}
            errorMessage={
              history.length === 0 && !isLoadingHistory
                ? "No uploads found"
                : ""
            }
          />
        </div>
      </div>
    </div>
  );
};

export default AssessmentRecapPage;
